import type { ChartV2Interval } from '../types/chart-v2.types';
import { Chip } from './Chip';
import { Dropdown } from './Dropdown';
import { ChevronDownIcon } from './Icons';
import styles from './IntervalSelector.module.scss';

/**
 * Props for `<IntervalSelector>`.
 *
 * Required: `intervals`, `selected`, `onChange`. Renders a chip-shaped
 * trigger that opens a menu of resample intervals; every row from `1h`
 * up is divided by a horizontal line.
 */
export interface IntervalSelectorProps {
  /** Intervals to render in the dropdown, in display order. */
  intervals: ChartV2Interval[];
  /** Currently active interval. */
  selected: ChartV2Interval;
  /**
   * Subset of `intervals` available for selection — anything outside the
   * list is rendered greyed-out. Useful when the backend returns empty
   * data for certain resamples on a given asset.
   */
  enabledIntervals?: ChartV2Interval[];
  /** Disables the trigger entirely. */
  disabled?: boolean;
  /** Called with the picked interval when the user selects a row. */
  onChange: (interval: ChartV2Interval) => void;
  /**
   * Per-interval label overrides shown inside the menu. Defaults to the
   * full English name (`"1 minute"`, `"1 day"`, …).
   */
  locales?: Partial<Record<ChartV2Interval, string>>;
  /**
   * Per-interval label overrides for the trigger chip only. Defaults to
   * `locales` (and then the English name) so passing this lets you show
   * a compact label on the trigger and a verbose one in the menu.
   */
  triggerLocales?: Partial<Record<ChartV2Interval, string>>;
}

export const DEFAULT_INTERVAL_LOCALES: Record<ChartV2Interval, string> = {
  '1m': '1 minute',
  '5m': '5 minutes',
  '15m': '15 minutes',
  '30m': '30 minutes',
  '1h': '1 hour',
  '1D': '1 day',
  '1W': '1 week',
  '1M': '1 month',
};

const SUB_HOURLY: ChartV2Interval[] = ['1m', '5m', '15m', '30m'];

/**
 * Chip-shaped trigger for picking a chart's resample interval (`"1m"`,
 * `"1D"`, `"1W"`, etc.). Opens a `Dropdown` menu showing every
 * interval in `intervals`, with a divider above every row from `1h`
 * up, matching v1's resample selector.
 *
 * Use `enabledIntervals` to render the unsupported intervals as
 * disabled rows; use `triggerLocales` to show a compact label on the
 * trigger and a more verbose one inside the menu.
 */
export const IntervalSelector = ({
  intervals,
  selected,
  enabledIntervals,
  disabled,
  onChange,
  locales,
  triggerLocales,
}: IntervalSelectorProps) => {
  const labelOf = (i: ChartV2Interval) =>
    locales?.[i] ?? DEFAULT_INTERVAL_LOCALES[i];

  const items = intervals.map((i, idx) => ({
    value: i,
    label: labelOf(i),
    disabled: enabledIntervals ? !enabledIntervals.includes(i) : false,
    // v1 borders every row from `1h` down (`li:nth-child(n + 5)` over its
    // fixed list), so each coarse resample gets its own divider.
    separated: idx > 0 && !SUB_HOURLY.includes(i),
  }));

  const triggerLabel = triggerLocales?.[selected] ?? labelOf(selected);

  return (
    <div className={styles.root} data-testid="chartv2-interval-selector">
      <Dropdown
        items={items}
        selected={selected}
        onChange={onChange}
        disabled={disabled}
        data-testid="chartv2-interval-dropdown"
        menuMinWidth={185}
        renderTrigger={({ toggle }) => (
          <Chip
            label={triggerLabel}
            rightIcon={<ChevronDownIcon />}
            disabled={disabled}
            onClick={toggle}
            data-testid="chartv2-interval-trigger"
          />
        )}
      />
    </div>
  );
};
